import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { usePackageTimeEntryDisplay } from '@/hooks/usePackageTimeEntryDisplay';
import {
  useFinalizeTimeEntry,
  useGetActiveTimeEntries,
  usePauseTimeEntry,
  useResumeTimeEntry,
} from '@/hooks/api/usePackageTimeEntries';
import { openPackageDetails } from '@/lib/packageNavigation';
import { Skeleton } from '@/components/ui/skeleton';
import type { PackageTimeEntry } from '@/types';
import { Clock3, ExternalLink, Loader2, Pause, Play, Square } from 'lucide-react';

function ActiveTimeEntryRow({
  entry,
  onFinalize,
}: {
  entry: PackageTimeEntry;
  onFinalize: (entry: PackageTimeEntry) => void;
}) {
  const displayTime = usePackageTimeEntryDisplay(entry);
  const pauseMutation = usePauseTimeEntry();
  const resumeMutation = useResumeTimeEntry();
  const isRunning = entry.status === 'running';

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">{entry.packageCode}</p>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="font-mono text-sm text-foreground">{displayTime}</span>
            <span>{isRunning ? 'Em andamento' : 'Pausado'}</span>
            {entry.stageNameAtCreation && <span>Etapa: {entry.stageNameAtCreation}</span>}
          </div>
          {entry.description && (
            <p className="mt-1 truncate text-xs text-muted-foreground">{entry.description}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => openPackageDetails(entry.packageId)}
        >
          <ExternalLink className="mr-2 h-4 w-4" />
          Abrir pacote
        </Button>

        {isRunning ? (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => pauseMutation.mutate({ packageId: entry.packageId, entryId: entry.id })}
            disabled={pauseMutation.isPending}
          >
            {pauseMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Pause className="mr-2 h-4 w-4" />
            )}
            Pausar
          </Button>
        ) : (
          <Button
            size="sm"
            className="w-full"
            onClick={() => resumeMutation.mutate({ packageId: entry.packageId, entryId: entry.id })}
            disabled={resumeMutation.isPending}
          >
            {resumeMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Play className="mr-2 h-4 w-4" />
            )}
            Retomar
          </Button>
        )}

        <Button
          size="sm"
          variant="outline"
          className="w-full border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700"
          onClick={() => onFinalize(entry)}
        >
          <Square className="mr-2 h-4 w-4" />
          Finalizar
        </Button>
      </div>
    </div>
  );
}

export function ActiveTimeEntriesMenu() {
  const { data: activeEntries = [], isLoading } = useGetActiveTimeEntries();
  const finalizeMutation = useFinalizeTimeEntry();
  const [entryToFinalize, setEntryToFinalize] = useState<PackageTimeEntry | null>(null);
  const [finalizeDescription, setFinalizeDescription] = useState('');
  const activeCount = activeEntries.length;
  const orderedEntries = useMemo(
    () =>
      [...activeEntries].sort((a, b) => {
        if (a.status === b.status) {
          return a.packageCode.localeCompare(b.packageCode);
        }

        return a.status === 'running' ? -1 : 1;
      }),
    [activeEntries]
  );

  const handleOpenFinalize = (entry: PackageTimeEntry) => {
    setEntryToFinalize(entry);
    setFinalizeDescription(entry.description || '');
  };

  const handleFinalize = () => {
    if (!entryToFinalize) {
      return;
    }

    finalizeMutation.mutate(
      {
        packageId: entryToFinalize.packageId,
        entryId: entryToFinalize.id,
        data: {
          description: finalizeDescription || entryToFinalize.description || undefined,
        },
      },
      {
        onSuccess: () => {
          setEntryToFinalize(null);
          setFinalizeDescription('');
        },
      }
    );
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative">
            <Clock3 className="h-5 w-5" />
            {activeCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
              >
                {activeCount > 99 ? '99+' : activeCount}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[460px]">
          <DropdownMenuLabel>
            {activeCount > 0
              ? `${activeCount} apontamento${activeCount > 1 ? 's' : ''} em andamento`
              : 'Apontamentos em andamento'}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          <div className="max-h-[420px] space-y-3 overflow-y-auto p-2">
            {isLoading ? (
              Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="space-y-2 rounded-md border p-3">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-3 w-32" />
                </div>
              ))
            ) : orderedEntries.length > 0 ? (
              orderedEntries.map((entry) => (
                <ActiveTimeEntryRow key={entry.id} entry={entry} onFinalize={handleOpenFinalize} />
              ))
            ) : (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Nenhum apontamento ativo no momento.
              </div>
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={!!entryToFinalize} onOpenChange={(open) => !open && setEntryToFinalize(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Finalizar apontamento</AlertDialogTitle>
            <AlertDialogDescription>
              {entryToFinalize
                ? `Finalize o apontamento do pacote ${entryToFinalize.packageCode}.`
                : 'Finalize o apontamento selecionado.'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <Input
            placeholder="Descricao do apontamento"
            value={finalizeDescription}
            onChange={(event) => setFinalizeDescription(event.target.value)}
          />

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleFinalize}
              disabled={finalizeMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {finalizeMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Confirmar finalizacao
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
